import { useCallback, useMemo, useRef, useState } from "react";
import apiClient from "../tools/axiosConfig";
import useLongAction, { actionFailure } from "../components/shared/long-action/useLongAction";
import { formatImportAnswer } from "../utils/importExportMessages";
import {
  formatUploadError,
  validateFile,
  validateFolderSize,
  BUNDLE_IMPORT_ACCEPT,
  BUNDLE_IMPORT_ACCEPTED_TYPES,
  BUNDLE_IMPORT_MAX_MB,
  LONG_REQUEST_TIMEOUT_MS,
} from "../utils/fileUploadHelpers";
import DependencyResolutionDialog from "../components/admin/course/edit-scenario/DependencyResolutionDialog";
import { previewRequiresUserInput } from "../utils/reflectionScenarioOptions";
import { buildBundleImportFormData } from "./useUnitTreeImport";

export function useCourseUnitImport({
  courseId,
  showError,
  onImported,
  inputId = null,
}) {
  const longAction = useLongAction();
  const unitInputRef = useRef(null);
  const unitFolderInputRef = useRef(null);
  const [importingUnit, setImportingUnit] = useState(false);
  const [unitMappingDialogOpen, setUnitMappingDialogOpen] = useState(false);
  const [unitPreviewData, setUnitPreviewData] = useState(null);
  // Holds the selected source ({ kind: 'file', file } | { kind: 'folder', files })
  // across preview -> dependency dialog -> perform.
  const [pendingUnitSource, setPendingUnitSource] = useState(null);

  const performUnitImport = useCallback(
    async (dependencyResolutions, contentResolutions, sourceOverride = null) => {
      const source = sourceOverride ?? pendingUnitSource;
      if (!source || !courseId) return;
      const inDialog = unitMappingDialogOpen && !sourceOverride;
      const failure = (error) => formatUploadError(error, "Failed to import unit");
      try {
        setImportingUnit(true);
        const task = async () => {
          const response = await apiClient.post(
            `/backoffice/v1/courses/${courseId}/import-unit/`,
            buildBundleImportFormData(source, dependencyResolutions || {}, contentResolutions || {}),
            { headers: { "Content-Type": "multipart/form-data" }, timeout: LONG_REQUEST_TIMEOUT_MS }
          );
          await onImported?.(response.data);
          return response;
        };
        if (inDialog) {
          const response = await task();
          setUnitMappingDialogOpen(false);
          longAction.showAnswer({ message: formatImportAnswer(response.data, "Unit imported successfully!") });
        } else {
          await longAction.run({
            label: "Importing the unit…", task, failure,
            success: (response) => formatImportAnswer(response.data, "Unit imported successfully!"),
          });
        }
      } catch (error) {
        if (inDialog) longAction.showAnswer(actionFailure(error, failure));
      } finally {
        setImportingUnit(false);
        setUnitMappingDialogOpen(false);
        setUnitPreviewData(null);
        setPendingUnitSource(null);
      }
    }, [courseId, longAction, onImported, pendingUnitSource, unitMappingDialogOpen]
  );

  const previewUnitSource = useCallback(
    async (source) => {
      if (!courseId) return;
      try {
        setImportingUnit(true);
        setPendingUnitSource(source);
        const formData = buildBundleImportFormData(source, null, null);
        const previewResp = await longAction.run({
          label: "Checking the unit import…",
          task: () => apiClient.post(
            `/backoffice/v1/courses/${courseId}/preview-unit-import/`, formData,
            { headers: { "Content-Type": "multipart/form-data" }, timeout: LONG_REQUEST_TIMEOUT_MS }
          ),
          success: () => null,
          failure: (error) => formatUploadError(error, "Couldn't preview unit import"),
        });
        if (!previewResp) return;
        const preview = previewResp?.data || {};
        setUnitPreviewData(preview);
        if (previewRequiresUserInput(preview)) {
          setUnitMappingDialogOpen(true);
          setImportingUnit(false);
        } else {
          await performUnitImport({}, {}, source);
        }
      } catch (err) {
        console.error(err);
        setImportingUnit(false);
        setPendingUnitSource(null);
      }
    },
    [courseId, longAction, performUnitImport]
  );

  const onUnitFileChange = useCallback(
    async (e) => {
      const unitFile = e.target.files?.[0];
      const reset = () => {
        try {
          if (e?.target) e.target.value = "";
        } catch (_) {}
      };
      if (!unitFile) {
        reset();
        return;
      }
      const validation = validateFile(unitFile, {
        maxSizeMB: BUNDLE_IMPORT_MAX_MB,
        acceptedTypes: BUNDLE_IMPORT_ACCEPTED_TYPES,
      });
      if (!validation.valid) {
        showError?.(validation.error);
        reset();
        return;
      }
      await previewUnitSource({ kind: "file", file: unitFile });
      reset();
    },
    [previewUnitSource, showError]
  );

  const onUnitFolderChange = useCallback(
    async (e) => {
      const files = e.target.files;
      const reset = () => {
        try {
          if (e?.target) e.target.value = "";
        } catch (_) {}
      };
      if (!files || files.length === 0) {
        reset();
        return;
      }
      const validation = validateFolderSize(files, BUNDLE_IMPORT_MAX_MB);
      if (!validation.valid) {
        showError?.(validation.error);
        reset();
        return;
      }
      await previewUnitSource({ kind: "folder", files: Array.from(files) });
      reset();
    },
    [previewUnitSource, showError]
  );

  const cancelUnitMapping = useCallback(() => {
    setUnitMappingDialogOpen(false);
    setUnitPreviewData(null);
    setPendingUnitSource(null);
    setImportingUnit(false);
  }, []);

  const requestImportUnit = useCallback(() => {
    if (!courseId) return;
    unitInputRef.current?.click();
  }, [courseId]);

  const requestImportUnitFolder = useCallback(() => {
    if (!courseId) return;
    unitFolderInputRef.current?.click();
  }, [courseId]);

  const importPortal = useMemo(
    () => (
      <>
        <input
          id={inputId || undefined}
          ref={unitInputRef}
          type="file"
          accept={BUNDLE_IMPORT_ACCEPT}
          onChange={onUnitFileChange}
          style={{ display: "none" }}
          tabIndex={-1}
          aria-hidden="true"
          disabled={importingUnit && !unitMappingDialogOpen}
        />
        <input
          ref={unitFolderInputRef}
          type="file"
          webkitdirectory=""
          directory=""
          multiple
          onChange={onUnitFolderChange}
          style={{ display: "none" }}
          tabIndex={-1}
          aria-hidden="true"
          disabled={importingUnit && !unitMappingDialogOpen}
        />
        <DependencyResolutionDialog
          open={unitMappingDialogOpen}
          onClose={cancelUnitMapping}
          onConfirm={performUnitImport}
          previewData={unitPreviewData}
          loading={importingUnit}
          entityLabel="Unit"
          entityNameKey="unit_name"
          confirmLabel="Import Unit"
        />
      </>
    ),
    [
      cancelUnitMapping,
      importingUnit,
      inputId,
      onUnitFileChange,
      onUnitFolderChange,
      performUnitImport,
      unitMappingDialogOpen,
      unitPreviewData,
    ]
  );

  return {
    importingUnit,
    requestImportUnit,
    requestImportUnitFolder,
    importPortal,
  };
}
